import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getPersonCostCentres, putPersonCostCentres } from '@/api/people';
import { getCodeBoard } from '@/api/baseplanCodes';
import { ApiError } from '@/api/client';
import type { CostCentreShare } from '@/types/oscar';

/**
 * Profile › Cost centre (Data-Model §4.5.5; Anthony 25/10/2026: "default ... but
 * override with a percentage split"). With no override the person costs 100% to
 * their PRIMARY BRANCH (team walk-through 26/10/2026 - not the location), else their
 * location's code; worked out live so it follows them. An override is a split that
 * totals 100%.
 */

const rowSchema = z.object({
  MinorCode: z.string().trim().min(1, 'Pick a cost centre'),
  Percentage: z.coerce.number().gt(0, 'More than 0').max(100, 'At most 100')
    .refine((n) => Math.round(n * 100) === n * 100, 'At most two decimals'),
});
export const splitSchema = z.object({ Rows: z.array(rowSchema).min(1, 'Add a cost centre') })
  .refine((v) => new Set(v.Rows.map((r) => r.MinorCode)).size === v.Rows.length, { message: 'A cost centre is listed twice', path: ['Rows'] })
  .refine((v) => Math.round(v.Rows.reduce((s, r) => s + r.Percentage, 0) * 100) === 10000, { message: 'The split must total 100%', path: ['Rows'] });
type SplitForm = z.infer<typeof splitSchema>;

const fmt = (s: CostCentreShare) => `${s.MinorCode}${s.Description ? ` ${s.Description}` : ''} — ${Number(s.Percentage)}%`;

export function CostCentreSplit({ personId, canEdit }: { personId: number; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const query = useQuery({ queryKey: ['person', personId, 'cost-centres'], queryFn: ({ signal }) => getPersonCostCentres(personId, signal) });
  const codesQuery = useQuery({ queryKey: ['locations', 'codes'], queryFn: ({ signal }) => getCodeBoard(signal), retry: false, enabled: editing });
  const save = useMutation({
    mutationFn: (rows: { MinorCode: string; Percentage: number }[]) => putPersonCostCentres(personId, rows),
    onSuccess: (data) => {
      queryClient.setQueryData(['person', personId, 'cost-centres'], data);
      // The person record (its CostCentreCode follows the split); exact, so the split just set is not refetched.
      queryClient.invalidateQueries({ queryKey: ['person', personId], exact: true });
      setEditing(false);
    },
  });

  const data = query.data;
  const form = useForm<SplitForm>({ resolver: zodResolver(splitSchema), defaultValues: { Rows: [] } });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'Rows' });
  const rows = form.watch('Rows');
  const total = rows.reduce((s, r) => s + (Number(r.Percentage) || 0), 0);

  function startEdit() {
    const start = data?.IsOverride ? data.Overrides : data?.Default ? [data.Default] : [];
    form.reset({ Rows: start.map((s) => ({ MinorCode: s.MinorCode, Percentage: Number(s.Percentage) })) });
    if (start.length === 0) append({ MinorCode: '', Percentage: 100 });
    save.reset();
    setEditing(true);
  }

  // Minor codes from the reporting hierarchy (all live codes if Baseplan's lists could not be read).
  const board = codesQuery.data;
  const options = (board?.Codes ?? [])
    .filter((c) => c.IsActive && (!board?.BaseplanFlagsAvailable || c.InGLMinor !== false))
    .sort((a, b) => a.Code.localeCompare(b.Code, undefined, { numeric: true }));

  return (
    <div className="flex gap-2 sm:col-span-2">
      <dt className="w-40 shrink-0 font-semibold text-darkGrey">Cost centre</dt>
      <dd className="min-w-0 flex-1 text-darkGrey/80">
        {query.isLoading && '…'}
        {query.isError && <span className="text-red">Could not load the cost centre.</span>}
        {data && !editing && (
          <>
            {data.Effective.length === 0
              ? <span>— <span className="text-xs text-darkGrey/70">(no primary branch or location code - set one, or a split)</span></span>
              : (
                <ul aria-label="Cost centre split">
                  {data.Effective.map((s) => <li key={s.MinorCode}>{fmt(s)}</li>)}
                </ul>
              )}
            <span className="block text-xs text-darkGrey/70">
              {data.IsOverride
                ? `Split set on the profile${data.Default ? ` (the default would be ${data.Default.MinorCode})` : ''}.`
                : data.Default?.Source === 'PrimaryBranch' ? 'From the primary branch.'
                : data.Default ? `From the location${data.LocationName ? ` (${data.LocationName})` : ''} - no primary branch is set.` : ''}
            </span>
            {canEdit && (
              <span className="mt-1 flex gap-3 text-xs">
                <button type="button" onClick={startEdit} className="font-semibold text-navy hover:underline">
                  {data.IsOverride ? 'Change split' : 'Split / override'}
                </button>
                {data.IsOverride && (
                  <button type="button" onClick={() => save.mutate([])} disabled={save.isPending} className="font-semibold text-navy hover:underline">
                    Use the default (no split)
                  </button>
                )}
              </span>
            )}
          </>
        )}
        {editing && (
          <form onSubmit={form.handleSubmit((v) => save.mutate(v.Rows.map((r) => ({ MinorCode: r.MinorCode, Percentage: Number(r.Percentage) }))))} className="space-y-2" aria-label="Cost centre split editor">
            {fields.map((f, i) => (
              <div key={f.id} className="flex items-center gap-2">
                <select aria-label={`Cost centre ${i + 1}`} {...form.register(`Rows.${i}.MinorCode`)} className="min-w-0 flex-1 rounded border border-border bg-white px-2 py-1 text-sm">
                  <option value="">—</option>
                  {/* keep a saved code selectable even if it is no longer offered */}
                  {rows[i]?.MinorCode && !options.some((o) => o.Code === rows[i].MinorCode) && <option value={rows[i].MinorCode}>{rows[i].MinorCode}</option>}
                  {options.map((c) => <option key={c.Code} value={c.Code}>{c.Code} {c.Description}</option>)}
                </select>
                <input aria-label={`Percentage ${i + 1}`} type="number" step="0.01" min="0" max="100" {...form.register(`Rows.${i}.Percentage`)} className="w-24 rounded border border-border bg-white px-2 py-1 text-right text-sm" />
                <span className="text-sm">%</span>
                {fields.length > 1 && <button type="button" onClick={() => remove(i)} className="text-xs font-semibold text-red hover:underline" aria-label={`Remove cost centre ${i + 1}`}>Remove</button>}
              </div>
            ))}
            <div className="flex items-center gap-3 text-xs">
              <button type="button" onClick={() => append({ MinorCode: '', Percentage: Math.max(0, Math.round((100 - total) * 100) / 100) })} className="font-semibold text-navy hover:underline">Add cost centre</button>
              <span className={Math.round(total * 100) === 10000 ? 'text-darkGrey/70' : 'font-semibold text-orange'} aria-label="Split total">Total {Math.round(total * 100) / 100}%</span>
            </div>
            {(form.formState.errors.Rows?.root?.message ?? form.formState.errors.Rows?.message) && (
              <p className="text-xs text-red">{form.formState.errors.Rows?.root?.message ?? form.formState.errors.Rows?.message}</p>
            )}
            {form.formState.errors.Rows?.map?.((e, i) => e && (
              <p key={i} className="text-xs text-red">Row {i + 1}: {e.MinorCode?.message ?? e.Percentage?.message}</p>
            ))}
            {save.isError && <p role="alert" className="text-xs text-red">{save.error instanceof ApiError ? save.error.message : 'Could not save the split.'}</p>}
            <div className="flex gap-2">
              <button type="submit" disabled={save.isPending} className="rounded bg-orange px-3 py-1 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50">Save split</button>
              <button type="button" onClick={() => setEditing(false)} className="rounded border border-border bg-white px-3 py-1 text-xs font-medium text-darkGrey hover:bg-bg">Cancel</button>
            </div>
          </form>
        )}
      </dd>
    </div>
  );
}
